import { ExternalLink, ChevronDown } from 'lucide-react'
import type { Lang } from '../../../lib/i18n'
import {
  type OrderDetail, type OrderStatusValue, getForwardAdvanceTargets, CANCELLABLE_FROM, formatOrderId,
} from '../../../lib/orders-api'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu'
import { WRITE_ROLES } from '../../../lib/auth'
import { Ltr } from '../Ltr'
import { formatDateTime } from '../format'
import { ot, FINAL_CHECK_ACTION_LABEL, STATUS_ADVANCE_LABEL, STATUS_BADGE_LABEL } from '../orders-i18n'
import { ConfirmDialog } from '../ConfirmDialog'
import { EditItemsDialog } from '../EditItemsDialog'

const AMENDABLE = new Set<OrderStatusValue>(['PENDING', 'CONFIRMED', 'IN_KITCHEN', 'AWAITING_RETAIL_PACK', 'AWAITING_FINAL_CHECK'])

const BADGE_VARIANT: Record<OrderStatusValue, 'default' | 'secondary' | 'sage' | 'destructive' | 'outline'> = {
  PENDING: 'outline',
  CONFIRMED: 'default',
  IN_KITCHEN: 'secondary',
  AWAITING_RETAIL_PACK: 'secondary',
  AWAITING_FINAL_CHECK: 'secondary',
  READY_FOR_DISPATCH: 'secondary',
  OUT_FOR_DELIVERY: 'default',
  DELIVERED: 'sage',
  CANCELLED: 'destructive',
  REFUNDED: 'destructive',
}

interface Props {
  order: OrderDetail
  lang: Lang
  roles: string[]
  displayStatus: OrderStatusValue
  pending: boolean
  onAdvance: (toStatus: OrderStatusValue) => void
  onCancel: () => Promise<void>
  onFinalCheck: () => void
  // R459: Edit items is online-only; a saved edit reloads the order.
  isOnline: boolean
  onItemsChanged: () => void
}

export function HeaderSection({ order, lang, roles, displayStatus, pending, onAdvance, onCancel, onFinalCheck, isOnline, onItemsChanged }: Props) {
  // M3 role model (reconciles #93 RBAC drift): write-capability = admin/owner/hop.
  // Phantom `operator` is gone — `hop` is the real operator role.
  const canWrite = roles.some((r) => (WRITE_ROLES as readonly string[]).includes(r))
  // bench-HOP-CAN-FINAL-CHECK.CB-F: Bilal's final check is a distinct action, not a
  // forward-advance (getForwardAdvanceTargets yields none for AWAITING_FINAL_CHECK —
  // only orders.service.ts finalCheck writes past it). Scoped to hop/admin, per the bench.
  const canFinalCheck = order.status === 'AWAITING_FINAL_CHECK' && roles.some((r) => r === 'hop' || r === 'admin')
  // bench-HOP-ORDER-BOARD.CB-A route-back (Rook #9167 gavel, MUST-FIX money-path leak):
  // raw FORWARD_TRANSITIONS let this surface offer READY_FOR_DISPATCH→OUT_FOR_DELIVERY and
  // OUT_FOR_DELIVERY→DELIVERED — delivery-leg writes that bypass driver+COD reconciliation.
  // getForwardAdvanceTargets() is the shared positive prep-lane allowlist (orders-api.ts).
  const forwardOptions = getForwardAdvanceTargets(order.status)
  const [primaryNext, ...altNext] = forwardOptions
  const trackHref = `/en/orders/${order.id}?t=${order.trackingToken}`

  return (
    <div className="rounded-xl border bg-card p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center gap-2.5">
            <Ltr className="text-2xl font-display font-semibold tracking-tight text-foreground sm:text-3xl">
              {formatOrderId(order.id, order.shortCode)}
            </Ltr>
            <Badge variant={BADGE_VARIANT[displayStatus]} className={pending ? 'animate-pulse' : ''}>
              {STATUS_BADGE_LABEL[displayStatus][lang]}
            </Badge>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            <span><Ltr>{formatDateTime(order.placedAt)}</Ltr></span>
            <span aria-hidden>·</span>
            <span>{ot(lang, 'channel')}: {order.channel}</span>
            <span aria-hidden>·</span>
            <span>{ot(lang, 'sourceSystem')}: {order.sourceSystem}</span>
            <span aria-hidden>·</span>
            <span>{ot(lang, 'fulfillmentMethod')}: {order.fulfillmentMethod === 'pickup' ? ot(lang, 'pickup') : ot(lang, 'delivery')}</span>
            <span aria-hidden>·</span>
            <span>{ot(lang, 'orderType')}: {order.orderType}</span>
          </div>
          <a
            href={trackHref}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground/70 underline-offset-2 hover:text-muted-foreground hover:underline"
          >
            {ot(lang, 'trackLink')}
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          {forwardOptions.length > 0 && (
            altNext.length > 0 ? (
              <div className="inline-flex">
                <Button
                  size="lg"
                  className="h-14 min-w-[48px] rounded-e-none px-6 text-base"
                  onClick={() => onAdvance(primaryNext)}
                  disabled={pending}
                >
                  {STATUS_ADVANCE_LABEL[primaryNext][lang]}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="lg" className="h-14 w-12 rounded-s-none border-s border-primary-foreground/20 px-0" disabled={pending}>
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {altNext.map((s) => (
                      <DropdownMenuItem key={s} onClick={() => onAdvance(s)}>
                        {STATUS_ADVANCE_LABEL[s][lang]}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <Button size="lg" className="h-14 px-6 text-base" onClick={() => onAdvance(primaryNext)} disabled={pending}>
                {STATUS_ADVANCE_LABEL[primaryNext][lang]}
              </Button>
            )
          )}

          {canFinalCheck && (
            <Button size="lg" className="h-14 px-6 text-base" onClick={onFinalCheck} disabled={pending}>
              {FINAL_CHECK_ACTION_LABEL[lang]}
            </Button>
          )}

          {canWrite && (AMENDABLE.has(order.status) || CANCELLABLE_FROM.has(order.status)) && (
            <div className="ms-2 flex items-center gap-2 border-s ps-2">
              {AMENDABLE.has(order.status) && (
                <EditItemsDialog
                  trigger={
                    <Button
                      variant="outline"
                      size="lg"
                      className="h-12 min-h-[48px] px-4"
                      disabled={pending || !isOnline}
                      aria-describedby={isOnline ? undefined : 'edit-items-offline'}
                    >
                      {ot(lang, 'amend')}
                    </Button>
                  }
                  order={order}
                  lang={lang}
                  isOnline={isOnline}
                  onSaved={onItemsChanged}
                />
              )}
              {AMENDABLE.has(order.status) && !isOnline && (
                <span id="edit-items-offline" className="text-xs text-muted-foreground">
                  {ot(lang, 'editItemsOffline')}
                </span>
              )}
              {CANCELLABLE_FROM.has(order.status) && (
                <ConfirmDialog
                  trigger={
                    <Button variant="destructive" size="lg" className="h-12 min-h-[48px] px-4">
                      {ot(lang, 'cancelOrder')}
                    </Button>
                  }
                  title={ot(lang, 'cancelConfirmTitle')}
                  description={ot(lang, 'cancelConfirmBody')}
                  actionLabel={ot(lang, 'cancelConfirmAction')}
                  dismissLabel={ot(lang, 'cancelConfirmDismiss')}
                  destructive
                  onConfirm={onCancel}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
